import { Component, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PartnerService, SalesConfigurationInterface } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { SubscriptionList } from '@vendasta/rx-utils';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { OrderSetupService } from './order-setup.service';

interface PartnerAndMarketId {
  partnerId: string;
  marketId?: string;
}

@Component({
  selector: 'app-order-setup',
  styleUrls: ['../order-config/order-config.component.scss'],
  templateUrl: './order-setup.component.html',
})
export class OrderSetupComponent implements OnDestroy {
  configForm: UntypedFormGroup;
  private readonly partnerId$: Observable<string>;
  private readonly marketId$: Observable<string>;
  getConfigIsReady$: Observable<boolean>;
  saveConfigIsLoading$: Observable<boolean>;
  private orderConfig$$ = new ReplaySubject<SalesConfigurationInterface>(1);
  private partnerAndMarketId$$ = new ReplaySubject<PartnerAndMarketId>(1);

  subscriptions = SubscriptionList.new();
  saveSuccessful$: Observable<boolean>;
  constructor(
    private readonly orderSetupService: OrderSetupService,
    private readonly partnerService: PartnerService,
    private readonly route: ActivatedRoute,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
  ) {
    this.partnerId$ = this.partnerService.getPartnerId();
    this.marketId$ = this.route.params.pipe(map((p) => p.marketId));

    const nextPartnerAndMarketId$ = combineLatest([this.partnerId$, this.marketId$]);
    this.subscriptions.add(nextPartnerAndMarketId$, ([partnerId, marketId]) => {
      const partnerAndMarketId = marketId ? { partnerId: partnerId, marketId: marketId } : { partnerId: partnerId };
      this.partnerAndMarketId$$.next(partnerAndMarketId);
    });

    this.setupGetOrderConfigs();
    this.setupSaveOrderConfigs();

    this.configForm = new UntypedFormGroup({
      orderSetupConfig: new UntypedFormGroup({
        hideSalesOrders: new UntypedFormControl(),
      }),
    });

    this.subscriptions.add(this.orderConfig$$, (config: SalesConfigurationInterface) =>
      this.configForm.get('orderSetupConfig.hideSalesOrders').setValue(config.hideOrders),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  private setupSaveOrderConfigs(): void {
    this.saveConfigIsLoading$ = this.partnerAndMarketId$$.pipe(
      switchMap((ids) => this.orderSetupService.isSaveOrderConfigLoading(ids.partnerId, ids.marketId)),
    );
    this.saveSuccessful$ = this.partnerAndMarketId$$.pipe(
      switchMap((ids) => this.orderSetupService.isSaveOrderConfigSuccessful(ids.partnerId, ids.marketId)),
    );
    this.addSaveStateSubscription();
  }

  private setupGetOrderConfigs(): void {
    this.getConfigIsReady$ = this.partnerAndMarketId$$.pipe(
      switchMap((ids) => this.orderSetupService.isGetOrderConfigReady(ids.partnerId, ids.marketId)),
    );
    const getSalesOrderConfig$ = this.partnerAndMarketId$$.pipe(
      tap((ids) => {
        this.orderSetupService.getSalesOrderConfig(ids.partnerId, ids.marketId);
      }),
      switchMap((ids) => this.orderSetupService.getSalesOrderConfigWorkResults(ids.partnerId, ids.marketId)),
    );
    this.subscriptions.add(getSalesOrderConfig$, (config: SalesConfigurationInterface) =>
      this.orderConfig$$.next(config),
    );
  }

  public saveConfig(): void {
    const currentConfig$ = this.orderConfig$$.pipe(take(1));
    this.subscriptions.add(currentConfig$, (config: SalesConfigurationInterface) =>
      this.updateConfigWithFormValueAndSave(config),
    );
  }

  private updateConfigWithFormValueAndSave(config: SalesConfigurationInterface): void {
    config.hideOrders = this.configForm.get('orderSetupConfig.hideSalesOrders').value as boolean;
    this.subscriptions.add(this.partnerAndMarketId$$.pipe(take(1)), (ids: PartnerAndMarketId) => {
      this.orderSetupService.saveSalesOrderConfig(ids.partnerId, ids.marketId, config);
    });
  }

  private addSaveStateSubscription(): void {
    this.subscriptions.add(this.saveSuccessful$, (isSuccessful: boolean) => {
      isSuccessful
        ? this.snackbarService.successSnack(this.translate.instant('SALES_ORDER_CONFIG_SETUP.ALERTS.SUCCESSFUL'))
        : this.snackbarService.errorSnack(this.translate.instant('SALES_ORDER_CONFIG_SETUP.ALERTS.UNSUCCESSFUL'));
      this.addSaveStateSubscription();
    });
  }
}
